'use client'

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { resolveRouteSkeleton } from '@/components/skeletons/RouteLoading'
import { registerNavigationPendingHandler } from '@/lib/navigation-pending'
import { SITE_PAGE_BG } from '@/lib/site-layout'
import { cn } from '@/lib/utils'

const SHOW_DELAY_MS = 40
const SAFETY_MS = 10_000

function normalizePath(path: string): string {
  if (!path) return '/'
  const bare = path.split('?')[0]?.split('#')[0] || '/'
  if (bare.length > 1 && bare.endsWith('/')) return bare.slice(0, -1)
  return bare
}

function currentKey(pathname: string, search: string): string {
  const p = normalizePath(pathname)
  return search ? `${p}?${search}` : p
}

function hrefKey(href: string): string | null {
  try {
    const url = new URL(href, window.location.origin)
    if (url.origin !== window.location.origin) return null
    const p = normalizePath(url.pathname)
    return url.search ? `${p}${url.search}` : p
  } catch {
    return null
  }
}

function pendingKeyFromRaw(raw: string): string | null {
  const fromUrl = hrefKey(raw)
  if (fromUrl) return fromUrl
  if (!raw.startsWith('/')) return null
  const bare = raw.split('#')[0] || '/'
  const [pathPart, qs] = bare.split('?')
  const p = normalizePath(pathPart || '/')
  return qs ? `${p}?${qs}` : p
}

/**
 * Dahili navigasyonda hedef path skeleton’ı.
 * Children her zaman mount kalır (RSC soft-nav uçuşunun commit olması için).
 * Skeleton üstte overlay olarak gösterilir — unmount eski “URL /’de skeleton takılır” bug’ını tetikliyordu.
 */
export default function NavigationSkeletonGate({
  children,
}: {
  children: ReactNode
}) {
  const pathname = usePathname() || '/'
  const searchParams = useSearchParams()
  const search = searchParams?.toString() || ''
  const urlKey = currentKey(pathname, search)

  const [pendingKey, setPendingKey] = useState<string | null>(null)
  const [skeletonPath, setSkeletonPath] = useState<string | null>(null)
  const [visible, setVisible] = useState(false)
  const showTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const safetyTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const fromKeyRef = useRef<string | null>(null)

  const clearTimers = () => {
    if (showTimer.current) clearTimeout(showTimer.current)
    if (safetyTimer.current) clearTimeout(safetyTimer.current)
    showTimer.current = null
    safetyTimer.current = null
  }

  const clearPending = () => {
    clearTimers()
    fromKeyRef.current = null
    setPendingKey(null)
    setSkeletonPath(null)
    setVisible(false)
  }

  const startPending = (raw: string) => {
    const key = pendingKeyFromRaw(raw)
    if (!key || key === urlKey) return
    clearTimers()
    fromKeyRef.current = urlKey
    setPendingKey(key)
    setSkeletonPath(normalizePath(key.split('?')[0] || '/'))
    setVisible(false)
    // scrollTo YAPMA — capture/click sırasında scroll soft-nav’i iptal edebiliyor
    showTimer.current = setTimeout(() => setVisible(true), SHOW_DELAY_MS)
    safetyTimer.current = setTimeout(clearPending, SAFETY_MS)
  }

  // Herhangi bir URL değişimi = navigasyon bitti (alias redirect dahil)
  useEffect(() => {
    if (!pendingKey) return
    if (fromKeyRef.current !== null && urlKey !== fromKeyRef.current) {
      clearPending()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlKey, pendingKey])

  useEffect(() => {
    registerNavigationPendingHandler(startPending)
    return () => registerNavigationPendingHandler(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlKey])

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented) return
      if (event.button !== 0) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

      const el = event.target as Element | null
      const anchor = el?.closest?.('a[href]') as HTMLAnchorElement | null
      if (!anchor) return
      if (anchor.hasAttribute('download')) return
      if (anchor.target && anchor.target !== '_self') return
      const rel = anchor.getAttribute('rel') || ''
      if (rel.includes('external')) return

      const rawHref = anchor.getAttribute('href') || ''
      if (rawHref.startsWith('#')) return

      const key = hrefKey(rawHref)
      if (!key) return

      // Capture’da hemen skeleton açma: Swiper preventClicks / defaultPrevented
      // sonrası iptal edilen tıklamalarda URL /’de skeleton takılıyordu.
      queueMicrotask(() => {
        if (event.defaultPrevented) return
        startPending(key)
      })
    }

    const onPopState = () => clearPending()

    document.addEventListener('click', onClick, true)
    window.addEventListener('popstate', onPopState)
    return () => {
      document.removeEventListener('click', onClick, true)
      window.removeEventListener('popstate', onPopState)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlKey])

  useEffect(() => () => clearTimers(), [])

  const showSkeleton = Boolean(pendingKey && visible && skeletonPath)

  // Navigasyon gerçekten başladıktan sonra (skeleton görünür) üste al — click anında değil
  useEffect(() => {
    if (!showSkeleton) return
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    } catch {
      /* ignore */
    }
  }, [showSkeleton])

  return (
    <div className="relative min-w-0">
      {/* Soft-nav uçuşu için children mount kalsın — unmount RSC’yi kilitleyebiliyordu */}
      <div
        className={showSkeleton ? 'invisible pointer-events-none select-none' : undefined}
        aria-hidden={showSkeleton || undefined}
      >
        {children}
      </div>
      {showSkeleton ? (
        <div
          className={cn(
            'absolute inset-0 z-[15] min-h-full overflow-y-auto',
            SITE_PAGE_BG,
          )}
          aria-live="polite"
          aria-busy="true"
        >
          {resolveRouteSkeleton(skeletonPath)}
        </div>
      ) : null}
    </div>
  )
}
